import type { Match, Matchday, Round } from '@uefadigital/panenka';
import { MatchDayType } from '@uefadigital/panenka';
import { PkButton, PkDropdown, PkIcon, PkMenu, PkTab, PkTabPanel, PkTabs } from '@uefadigital/panenka-react';
import i18n from 'i18next';
import type { SetStateAction, Dispatch } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCompetitionStructureQuery, useGetMatchUnitByIdQuery } from 'api/fspApi';
import { MatchUnit } from 'components/MatchUnit';
import useIsDesktop from 'hooks/useIsDesktop';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

interface NormalizedMatchesData {
  [key: string]: {
    matches: Array<Match>;
    date: number;
  };
}

interface MatchDayData {
  matchData: Match;
}

type Date = {
  day: 'numeric' | '2-digit' | undefined;
  month: 'numeric' | 'long' | '2-digit' | 'short' | 'narrow' | undefined;
};

const setInitialActiveTab = (
  matchDaysObj: NormalizedMatchesData,
  language: string,
  options: Date,
  setSelectedTab: Dispatch<SetStateAction<number | null>>,
) => {
  const dateNow = new Date().toLocaleDateString(language, options).split(' ').reverse().join(' ');

  const matchDaysTabArray = Object.entries(matchDaysObj);

  const newDate = Date.now();
  const matchDayDate = matchDaysTabArray[matchDaysTabArray.length - 1]?.[1].date;

  if (dateNow in matchDaysObj) {
    const tabIndex = matchDaysTabArray.findIndex((el) => el[0] === dateNow) || 0;

    return setSelectedTab(tabIndex);
  } else if (newDate > matchDayDate) {
    return setSelectedTab(matchDaysTabArray.length - 1);
  }

  return setSelectedTab(0);
};

export const Matches = ({ matchData }: MatchDayData) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslation();

  const [displayOverlay, setDisplayOverlay] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number | null>(-1);

  const dropdownTriggerRef = useRef<HTMLPkButtonElement>(null);
  const dropdownRef = useRef<HTMLPkDropdownElement>(null);

  const {
    data: competition = [],
    error: competitionIsError,
    isLoading,
  } = useGetCompetitionStructureQuery({
    id: matchData?.competition?.id,
    seasonYear: matchData?.seasonYear,
  });

  const createRoundName = useCallback(
    (round: Round | undefined, matchday: Matchday | undefined) => {
      let roundName = translateEntity(round)?.name;

      if (round?.mode === 'GROUP') {
        roundName = `${translateEntity(round)?.name} - ${translateEntity(matchday)?.longName}`;
      } else if (round?.mode === 'KNOCK_OUT' && round?.modeDetail === 'KNOCK_OUT_TWO_LEGS') {
        const sequenceNumber = matchday?.type === MatchDayType.FIRST_LEG ? 1 : 2;

        roundName = `${translateEntity(round)?.shortName} - ${t(`${sequenceNumber}_leg`)}`;
      }

      return roundName;
    },
    [t],
  );

  const roundName = createRoundName(matchData.round, matchData.matchday);

  const matchdays = [...(competition?.[0]?.matchdays ?? [])];

  const matchDayId = matchdays
    .filter((matchday: Matchday) => {
      return (
        createRoundName(matchData.round, matchday) === roundName && matchData?.matchday?.roundId === matchday.roundId
      );
    })
    .map((matchDay) => matchDay.id);

  const language = i18n.language.toUpperCase();

  const {
    data: matches = [],
    isError: matchesError,
    isLoading: matchesLoading,
    isFetching: matchesFetching,
  } = useGetMatchUnitByIdQuery(
    {
      matchDayId,
    },
    {
      skip: !matchDayId.length,
    },
  );

  const normalizedMatchesObj = useMemo(
    () =>
      matches.toReversed().reduce((acc: NormalizedMatchesData, el) => {
        const start = new Date(el?.kickOffTime?.date ?? '');
        const options: Date = {
          day: 'numeric',
          month: isDesktop ? 'long' : 'short',
        };
        const date = start.toLocaleDateString(language, options).split(' ');
        const updatedDate = `${date[1]} ${date[0]}`;

        acc[updatedDate] =
          updatedDate in acc
            ? {
                matches: [...acc[updatedDate].matches, el],
                date: start.getTime(),
              }
            : { matches: [el], date: start.getTime() };

        return acc;
      }, {}),
    [matches, language, isDesktop],
  );

  useEffect(() => {
    if (selectedTab === -1) {
      setInitialActiveTab(
        normalizedMatchesObj,
        language,
        {
          day: 'numeric',
          month: isDesktop ? 'long' : 'short',
        },
        setSelectedTab,
      );
    }
  }, [isDesktop, language, normalizedMatchesObj, selectedTab]);

  useEffect(() => {
    setInitialActiveTab(
      normalizedMatchesObj,
      language,
      {
        day: 'numeric',
        month: isDesktop ? 'long' : 'short',
      },
      setSelectedTab,
    );
  }, [isDesktop, language, normalizedMatchesObj]);

  const displayMatchDaysTab = (matches: NormalizedMatchesData) => {
    return Object.entries(matches).map((day, i) => {
      const selected = i === selectedTab;

      return (
        <PkTab
          tabId={day[0]}
          key={day[0]}
          selected={selected}
          onPkTabButtonClick={() => {
            setSelectedTab(i);
          }}
        >
          <div className="pk-col pk-col--span-12 pk-mb--0">
            <p
              className={`${
                isDesktop ? 'pk-line-height--md' : 'pk-line-height--sm'
              } pk-font-size--m pk-font-weight--bold`}
            >
              {day[0]}
            </p>
          </div>
        </PkTab>
      );
    });
  };

  const displayMatchesTab = (matches: NormalizedMatchesData) => {
    return Object.entries(matches).map((day, i) => {
      const active = i === selectedTab;

      return (
        <PkTabPanel tab={day[0]} slot="content" key={day[0]} active={active}>
          <div className={style.tabPanelContent}>
            {day[1].matches.map((el) => (
              <div
                className="pk-bg--elevation-01"
                key={el.id}
                onClick={() => {
                  dropdownRef?.current?.hide();
                }}
              >
                <MatchUnit
                  match={el}
                  isDesktop={isDesktop}
                  hasDivider={false}
                  isFixturesPage={false}
                  hideMatchUnitDetail={false}
                />
              </div>
            ))}
          </div>
        </PkTabPanel>
      );
    });
  };

  useEffect(() => {
    const dropdown = document.getElementById('dropDown');
    const dropdownRefCurrent = dropdownRef.current;
    const dropdownTriggerRefCurrent = dropdownTriggerRef.current;

    dropdown?.addEventListener('pkHide', () => {
      document.body.classList.remove(style.overflowBlock);
      dropdownRefCurrent?.classList.remove(style.dropdownOverlay);
      setDisplayOverlay(false);
    });

    dropdown?.addEventListener('pkShow', () => {
      dropdownTriggerRefCurrent?.scrollIntoView();
      document.body.classList.add(style.overflowBlock);
      dropdownRefCurrent?.classList.add(style.dropdownOverlay);
      setDisplayOverlay(true);
    });

    return () => {
      dropdown?.removeEventListener('pkHide', () => {
        document.body.classList.remove(style.overflowBlock);
        dropdownRefCurrent?.classList.remove(style.dropdownOverlay);
        setDisplayOverlay(false);
      });

      dropdown?.removeEventListener('pkShow', () => {
        dropdownTriggerRefCurrent?.scrollIntoView();
        document.body.classList.add(style.overflowBlock);
        dropdownRefCurrent?.classList.add(style.dropdownOverlay);
        setDisplayOverlay(true);
      });
    };
  }, []);

  if (
    isLoading ||
    !competition ||
    !!competitionIsError ||
    !matches?.length ||
    matchesFetching ||
    matchesLoading ||
    matchesError
  ) {
    return <div />;
  }

  return (
    <>
      {displayOverlay && (
        <div id="overlay" className={`${isDesktop ? style.overlay : style.overlayMobile} pk-bg--elevation-01`} />
      )}
      {isDesktop ? (
        <PkDropdown hideArrow className="pk-bg-primary-01" id="dropDown" ref={dropdownRef} closeOnSelect={false}>
          <PkButton slot="trigger" appearance="outlined" width="130" ref={dropdownTriggerRef} high-contrast>
            {t('matches')}
            {displayOverlay ? (
              <PkIcon name="navigation-chevron-up" className="pk-ml--xs" />
            ) : (
              <PkIcon name="navigation-chevron-down" className="pk-ml--xs" />
            )}
          </PkButton>
          <PkMenu hideSectionDivider className={`${style.menu} store-dropdown__menu pk-bg--elevation-01`}>
            <PkTabs alignment="left">
              {displayMatchDaysTab(normalizedMatchesObj)}
              {displayMatchesTab(normalizedMatchesObj)}
            </PkTabs>
            <div className="pk-text--center" slot="footer">
              <PkButton
                appearance="outlined"
                className="pk-justify-content--center"
                onClick={() => {
                  dropdownRef?.current?.hide();
                }}
              >
                {t('close')}
                <PkIcon name="navigation-chevron-up" className="pk-ml--xs" />
              </PkButton>
            </div>
          </PkMenu>
        </PkDropdown>
      ) : (
        <PkDropdown
          hideArrow
          className={`${displayOverlay ? style.dropDownMobile : ''} pk-w--100 pk-w-sm--auto pk-bg-primary-04`}
          id="dropDown"
          ref={dropdownRef}
          closeOnSelect={false}
          panelHeight="auto"
        >
          <PkButton
            slot="trigger"
            appearance="outlined"
            ref={dropdownTriggerRef}
            onPkClick={() => setDisplayOverlay(!displayOverlay)}
            className="pk-w--100"
            width="auto"
          >
            {t('matches')}
            {displayOverlay ? (
              <PkIcon name="navigation-chevron-up" className="pk-ml--xs" />
            ) : (
              <PkIcon name="navigation-chevron-down" className="pk-ml--xs" />
            )}{' '}
          </PkButton>
          <PkMenu hideSectionDivider className={`${style.menu} store-dropdown__menu`}>
            <PkTabs alignment="left">
              {displayMatchDaysTab(normalizedMatchesObj)}
              {displayMatchesTab(normalizedMatchesObj)}
            </PkTabs>
          </PkMenu>
          <PkButton
            appearance="outlined"
            className={displayOverlay ? style.triggerCloseMobile : 'pk-d--none'}
            onPkClick={() => {
              dropdownRef?.current?.hide();
            }}
          >
            {t('close')}
            <PkIcon name="navigation-chevron-up" className="pk-ml--xs" />
          </PkButton>
        </PkDropdown>
      )}
    </>
  );
};
